/* eslint max-lines: ["error", { max: 280}] -- high number of utility methods in this file */
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import {
  DeviceType,
  PatientTag,
  PatientTreatmentPhases,
  UniversalStatusCode,
} from "@/constants/User";
import { getUserTreatmentPhase } from "@/hooks/usePatientStatus";
import type { UserTabSelectorTabProps } from "@/ui/core/UserTabSelector";

const getInitialsFromName = (name: string): string => {
  if (isEmpty(name)) {
    return "";
  }

  const [firstName, lastName] = name.split(" ");

  if (!lastName) {
    return firstName.charAt(0).toUpperCase();
  }

  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

const getPreferredFirstName = ({
  chosenName,
  firstName,
}: {
  chosenName?: string;
  firstName: string;
}): string => {
  const normalizedFirstName = firstName.trim();

  return chosenName?.trim() || normalizedFirstName;
};

const getPreferredFullName = ({
  chosenName,
  firstName,
  lastName,
}: {
  chosenName?: string;
  firstName: string;
  lastName: string;
}): string => {
  const preferredFistName = getPreferredFirstName({ chosenName, firstName });
  const normalizedLastName = lastName.trim();

  return `${preferredFistName} ${normalizedLastName}`;
};

const getIsPatientInTreatment = ({
  adminEpisodeStatus,
  patientTags,
}: {
  adminEpisodeStatus?: AdminEpisodeStatus;
  patientTags: string[];
}): boolean => {
  const isInTreatment = adminEpisodeStatus === "In-treatment";
  const hasTreatmentTag = patientTags.includes(PatientTag.EquipAppTreatment);
  return isInTreatment && hasTreatmentTag;
};

const linkedPatientToUserAvatar = (
  patient: LinkedPatientProfile,
): BaseUserAvatarProps => {
  const { chosenName, externalId, firstName, lastName } = patient;

  return {
    userId: externalId,
    userName: getPreferredFullName({ chosenName, firstName, lastName }),
    userType: "patient",
  };
};

const getTabSelectorOptions = ({
  appUserCount,
  appUserId,
  isAppUserPatient,
  patients,
  patientsCounters,
  shouldIncludeSelf,
}: {
  appUserCount?: number;
  appUserId: string;
  isAppUserPatient: boolean;
  patients?: LinkedPatientProfile[];
  patientsCounters?: Record<string, number>;
  shouldIncludeSelf?: boolean;
}): undefined | UserTabSelectorTabProps[] => {
  if (isAppUserPatient) {
    return undefined;
  }

  const options: UserTabSelectorTabProps[] = [];

  if (shouldIncludeSelf) {
    options.push({
      badgeCount: appUserCount,
      universalStatusCode: UniversalStatusCode.Unknown,
      userId: appUserId,
      userName: "You",
    });
  }

  patients?.forEach((patient) => {
    const { chosenName, externalId, firstName, universalStatusCode } = patient;
    options.push({
      badgeCount: patientsCounters?.[externalId],
      universalStatusCode,
      userId: externalId,
      userName: getPreferredFirstName({ chosenName, firstName }),
    });
  });

  return options;
};

const getIsSelectedUserInTreatment = ({
  hasLinkedPatientInTreatment,
  isAppUserPatient,
  isCurrentUserInTreatment,
  selectedUserStatusCode,
}: {
  hasLinkedPatientInTreatment?: boolean;
  isAppUserPatient: boolean;
  isCurrentUserInTreatment?: boolean;
  selectedUserStatusCode?: UniversalStatusCode;
}): boolean => {
  // This function checks if the user is in treatment based on various conditions.
  // It returns true if the user is a patient in treatment, a support user with a patient in treatment,
  // or a support user with an unknown status but has a linked patient in treatment.
  const selectedUserStatus = getUserTreatmentPhase(selectedUserStatusCode);

  const isPatientInTreatment =
    isAppUserPatient && Boolean(isCurrentUserInTreatment);

  const isSupportPatientInTreatment =
    !isAppUserPatient &&
    selectedUserStatus === PatientTreatmentPhases.InTreatment;

  const isSupportItselfWithUnknownStatus =
    !isAppUserPatient &&
    Boolean(hasLinkedPatientInTreatment) &&
    selectedUserStatusCode === UniversalStatusCode.Unknown;

  return (
    isPatientInTreatment ||
    isSupportPatientInTreatment ||
    isSupportItselfWithUnknownStatus
  );
};

const formatAddress = ({
  address,
  shouldDivideWithNewline = true,
}: {
  address: GooglePlacesAddress;
  shouldDivideWithNewline?: boolean;
}): string => {
  type AddressPart = null | string | undefined;
  const formatPartialAddress = (partialAddress: AddressPart[]) => {
    return partialAddress
      .filter((d) => !isNil(d))
      .map((d) => d.trim())
      .filter((d) => !isEmpty(d))
      .join(", ");
  };
  const firstPart = formatPartialAddress([
    address.addressLine1,
    address.addressLine2,
  ]);
  const secondPart = formatPartialAddress([
    address.city,
    address.state,
    address.zip,
  ]);
  const divider = shouldDivideWithNewline ? "\n" : " ";

  return [
    firstPart,
    isEmpty(firstPart) || isEmpty(secondPart) ? "" : `,${divider}`,
    secondPart,
  ].join("");
};

const getBasicInfo = (profile: FullProfileResponse): UserBasicInfo => {
  const { chosenName, firstName, gender, lastName, pronouns } = profile;
  return {
    chosenName,
    firstName,
    gender,
    lastName,
    pronouns,
  };
};

const getContactInfo = (profile: FullProfileResponse): UserContactInfo => {
  const { address, cellPhone, contactPreferences, email } = profile;
  return {
    addressLine1: address?.addressLine1,
    addressLine2: address?.addressLine2,
    cellPhone,
    city: address?.city,
    contactPreferences,
    email,
    state: address?.state,
    zip: address?.zip,
  };
};

const getOtherInfo = (profile: FullProfileResponse): UserOtherInfo => {
  const { dateOfBirth, languages, relationToPatient } = profile;
  return {
    dateOfBirth,
    languages,
    relationToPatient,
  };
};

const getNotificationSettings = (
  profile?: FullProfileResponse | null,
): NotificationSettings => {
  const defaultSettings: NotificationSettings = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    receiveEmails: false,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    receivePush: false,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    receiveSms: false,
  };

  if (isNil(profile?.preferences?.notifications)) {
    return defaultSettings;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { receiveEmails, receivePush, receiveSms } =
    profile.preferences.notifications;
  return {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    receiveEmails,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    receivePush,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    receiveSms,
  };
};

const getIsOnboardedOnMobile = (onboardings?: UserOnboarding[]) => {
  const mobileOnboarding = onboardings?.find(
    (onboarding: UserOnboarding) => onboarding.deviceType === DeviceType.Mobile,
  );
  return !isEmpty(mobileOnboarding);
};
const getIsWeightInvalid = (value: string): boolean => {
  const parsedWeight = Number(value);
  return !value || !Number.isFinite(parsedWeight) || parsedWeight <= 0;
};

export {
  formatAddress,
  getBasicInfo,
  getContactInfo,
  getInitialsFromName,
  getIsOnboardedOnMobile,
  getIsPatientInTreatment,
  getIsSelectedUserInTreatment,
  getIsWeightInvalid,
  getNotificationSettings,
  getOtherInfo,
  getPreferredFirstName,
  getPreferredFullName,
  getTabSelectorOptions,
  linkedPatientToUserAvatar,
};
